import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';

interface ManualBookingLinkDialogProps {
  open: boolean;
  bookingLinkUrl: string | null;
  copied: boolean;
  onCopy: () => void;
  onClose: () => void;
}

export default function ManualBookingLinkDialog({
  open,
  bookingLinkUrl,
  copied,
  onCopy,
  onClose,
}: ManualBookingLinkDialogProps) {
  const { t } = useTranslation();

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t('spaceCalendar.bookingLinkDialogTitle', 'Booking created')}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t('spaceCalendar.bookingLinkDialogBody', 'Share this link with your guest:')}
        </Typography>
        <TextField
          value={bookingLinkUrl ?? ''}
          fullWidth
          InputProps={{ readOnly: true }}
          sx={{
            mb: 2,
            '& .MuiInputBase-input': {
              fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
              fontSize: '0.875rem',
            },
          }}
        />
        {copied ? (
          <Alert severity="success" sx={{ borderRadius: 2 }}>
            {t('spaceCalendar.bookingLinkCopied', 'Link copied')}
          </Alert>
        ) : null}
      </DialogContent>
      <DialogActions sx={{ p: 2, gap: 1 }}>
        <Button onClick={onClose}>{t('common.close', 'Close')}</Button>
        <Button
          variant="contained"
          startIcon={<ContentCopyIcon />}
          onClick={onCopy}
          disabled={!bookingLinkUrl}
        >
          {t('spaceCalendar.copyBookingLink', 'Copy link')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
